import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { useEnquiryPopup } from "@/contexts/EnquiryPopupContext";

const popupSchema = z.object({
  fullName: z.string().min(2, "Please enter your full name").max(100, "Name must be less than 100 characters"),
  destination: z.string().min(2, "Please enter a destination"),
  travelDates: z.string().min(1, "Please select travel dates"),
  travelers: z.coerce.number().int().positive().max(50),
  budget: z.string().optional().or(z.literal("")),
  mobile: z.string().regex(/^\+91\d{10}$/, "Please enter +91 followed by 10 digits"),
  email: z.string().email("Please enter a valid email").optional().or(z.literal("")),
  city: z.string().max(100).optional(),
  whatsappConsent: z.boolean().default(true),
  website: z.string().optional(), // honeypot
});

type PopupFormData = z.infer<typeof popupSchema>;

const POPUP_INITIAL_DELAY_MS = 8000; // Show after 8s on first page visit only
const POPUP_SESSION_KEY = "ffh_popup_shown_session"; // sessionStorage: cleared per browser session
const POPUP_SUBMITTED_KEY = "ffh_has_submitted_enquiry"; // localStorage: never show again after submit

/**
 * EnquiryPopup Component
 *
   * Smart Popup Logic — once-per-session:
   * Rule A: If user has ever submitted (localStorage), never show again.
   * Rule B: Otherwise, show ONCE per browser session, after 8s on the first page visit.
   *         Subsequent navigations within the same session never re-trigger it.
 * 
 * EXCEPTION: Opens INSTANTLY when triggered externally via useEnquiryPopup().openWithDestination()
 */
export const EnquiryPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shakeFields, setShakeFields] = useState<string[]>([]);
  const { toast } = useToast();
  const { isExternalOpen, prefilledDestination, closeExternal } = useEnquiryPopup();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerShake = (fieldNames: string[]) => {
    setShakeFields(fieldNames);
    setTimeout(() => setShakeFields([]), 500);
  };

  const shakeAnimation = {
    x: [-5, 5, -5, 5, 0],
    transition: { duration: 0.4 }
  };

  const form = useForm<PopupFormData>({
    resolver: zodResolver(popupSchema),
    defaultValues: {
      fullName: "",
      destination: "",
      travelDates: "",
      travelers: 2,
      budget: "",
      mobile: "+91",
      email: "",
      city: "",
      whatsappConsent: true,
      website: "",
    },
  });

  // Clear timer
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Schedule popup to show after delay
  const schedulePopup = useCallback((delayMs: number) => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      setIsOpen(true);
    }, delayMs);
  }, [clearTimer]);

  // Handle external trigger from BudgetCalculator (opens INSTANTLY with pre-filled destination)
  useEffect(() => {
    if (isExternalOpen && prefilledDestination) {
      // Pre-fill the destination field
      form.setValue("destination", prefilledDestination);
      setIsOpen(true);
    }
  }, [isExternalOpen, prefilledDestination, form]);

  // Smart popup logic — once per browser session
  useEffect(() => {
    // Skip automatic popup if externally triggered (handled by separate effect)
    if (isExternalOpen) return;

    // Rule A: never show if previously submitted
    if (localStorage.getItem(POPUP_SUBMITTED_KEY) === "true") {
      setHasSubmitted(true);
      return;
    }

    // Rule B: only schedule on the very first page visit of this session
    if (sessionStorage.getItem(POPUP_SESSION_KEY) === "true") {
      return; // Already shown (or scheduled) earlier in this session
    }

    // Mark as scheduled immediately so navigation between pages doesn't re-trigger
    sessionStorage.setItem(POPUP_SESSION_KEY, "true");
    schedulePopup(POPUP_INITIAL_DELAY_MS);

    return () => {
      clearTimer();
    };
  }, [schedulePopup, clearTimer, isExternalOpen]);

  // Handle dialog close (not from submit)
  const handleOpenChange = (open: boolean) => {
    if (!open && !hasSubmitted) {
      setIsOpen(false);
      closeExternal();
      // Once-per-session: closing also counts as "shown" — do not re-schedule.
      sessionStorage.setItem(POPUP_SESSION_KEY, "true");
      clearTimer();
    } else {
      setIsOpen(open);
    }
  };

  const handleFormSubmit = async (data: PopupFormData) => {
    // Check honeypot
    if (data.website) return;

    setIsSubmitting(true);

    try {
      const { supabase } = await import("@/integrations/supabase/client");
      
      const { error } = await supabase.functions.invoke('submit-enquiry', {
        body: {
          name: data.fullName,
          mobile: data.mobile,
          email: data.email || null,
          city: data.city || null,
          destination: data.destination,
          month: data.travelDates || null,
          travelers: data.travelers || null,
          tripType: null,
          message: data.budget ? `Budget: ${data.budget}` : null,
          website: data.website || null,
        }
      });

      if (error) throw error;

      toast({
        title: "Thank you!",
        description: "Our team will call you shortly.",
      });

      // Mark as submitted - don't show again
      setHasSubmitted(true);
      localStorage.setItem(POPUP_SUBMITTED_KEY, "true");
      sessionStorage.setItem(POPUP_SESSION_KEY, "true");
      clearTimer();
      setIsOpen(false);
      
      form.reset({
        fullName: "",
        destination: "",
        travelDates: "",
        travelers: 2,
        budget: "",
        mobile: "+91",
        email: "",
        city: "",
        whatsappConsent: true,
        website: "",
      });
    } catch (error) {
      toast({
        title: "Submission failed",
        description: "Please try again or call us directly at +91 99246 19959.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = form.handleSubmit(handleFormSubmit, (errors) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      triggerShake(errorFields);
    }
  });

  // Don't render if already submitted
  if (hasSubmitted) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" aria-describedby="enquiry-popup-description">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading text-center">Plan Your Dream Trip 🌴</DialogTitle>
          <DialogDescription id="enquiry-popup-description" className="text-center text-muted-foreground text-sm">
            Tell us your travel plans and get a personalized quote
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4">
            {/* Honeypot */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden"
              {...form.register("website")}
            />

            {/* Full Name - Required */}
            <FormField
              control={form.control}
              name="fullName"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Full Name *</FormLabel>
                  <FormControl>
                    <motion.div animate={shakeFields.includes("fullName") ? shakeAnimation : {}}>
                      <Input 
                        placeholder="Enter your full name" 
                        autoComplete="name" 
                        className={fieldState.error ? "border-destructive" : ""}
                        {...field} 
                      />
                    </motion.div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="destination"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Where do you want to go? *</FormLabel>
                  <FormControl>
                    <motion.div animate={shakeFields.includes("destination") ? shakeAnimation : {}}>
                      <Input 
                        placeholder="Enter destination" 
                        className={fieldState.error ? "border-destructive" : ""}
                        {...field} 
                      />
                    </motion.div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="travelDates"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>When? *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="jan-feb">Jan - Feb 2026</SelectItem>
                        <SelectItem value="mar-apr">Mar - Apr 2026</SelectItem>
                        <SelectItem value="may-jun">May - Jun 2026</SelectItem>
                        <SelectItem value="jul-aug">Jul - Aug 2026</SelectItem>
                        <SelectItem value="sep-oct">Sep - Oct 2026</SelectItem>
                        <SelectItem value="nov-dec">Nov - Dec 2026</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="travelers"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Travelers *</FormLabel>
                    <Select onValueChange={(val) => field.onChange(Number(val))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <SelectItem key={n} value={n.toString()}>
                            {n} {n === 1 ? "Person" : "People"}
                          </SelectItem>
                        ))}
                        <SelectItem value="15">10+ People</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="budget"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Budget Range (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select budget (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="under-50k">Under ₹50,000</SelectItem>
                      <SelectItem value="50k-1l">₹50,000 - ₹1 Lakh</SelectItem>
                      <SelectItem value="1l-2l">₹1 - 2 Lakhs</SelectItem>
                      <SelectItem value="2l-5l">₹2 - 5 Lakhs</SelectItem>
                      <SelectItem value="above-5l">Above ₹5 Lakhs</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="mobile"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Phone Number *</FormLabel>
                  <FormControl>
                    <motion.div animate={shakeFields.includes("mobile") ? shakeAnimation : {}}>
                      <Input 
                        type="tel" 
                        maxLength={13} 
                        className={fieldState.error ? "border-destructive" : ""}
                        {...field} 
                      />
                    </motion.div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email - Optional */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email (Optional)</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* City - Optional */}
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Surat, Mumbai" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="whatsappConsent"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className="text-sm font-normal cursor-pointer">Contact me on WhatsApp</FormLabel>
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full min-h-[44px]" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Get Free Quote"
              )}
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Our travel team will contact you shortly to help plan your trip.
            </p>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
